import { FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { FooterProps } from '../types'
import SocialLinksComponent from './social-links'
import { useLocale } from '../../../../hooks/useLocale'
import { Link } from 'gatsby'
import { Button } from '../../../atoms/button'
import { Text } from '../../../atoms/text'
import LinkLists from './link-lists'

const MainFooterSection: FunctionComponent<Omit<FooterProps, 'legalLinks'>> = ({
  logo,
  linkLists,
  socialLinks,
  cta1,
  cta2,
}) => {
  const { locale } = useLocale()
  const intl = useIntl()
  return (
    <Wrapper>
      <LogoLink to={`/${locale}/`} title={intl.formatMessage({ id: 'link.backToHomepage' })}>
        {!logo._isFallback && <Logo src={logo.url} alt={logo.alternativeText ?? ''} />}
      </LogoLink>
      <LinkLists linkLists={linkLists} />
      <StyledSocialLinks links={socialLinks} />
      {cta1.title && (
        <CTA1
          variant="outlined"
          themeOverride="background_is_lighter"
          trailingIcon="right-arrow"
          animation={true}
          as={cta1.openInNewTab ? 'a' : Link}
          {...(cta1.openInNewTab ? { href: `${cta1.link}` } : { to: `${cta1.link}` })}
          target={cta1.openInNewTab ? '_blank' : '_self'}
          rel={cta1.openInNewTab ? 'noopener noreferrer' : undefined}
        >
          <Text fontStyle="label-s">{cta1.title}</Text>
        </CTA1>
      )}
      {cta2.title && (
        <CTA2
          variant="outlined"
          themeOverride="background_is_lighter_secondary"
          as={cta1.openInNewTab ? 'a' : Link}
          {...(cta2.openInNewTab ? { href: `${cta2.link}` } : { to: `${cta2.link}` })}
          target={cta2.openInNewTab ? '_blank' : '_self'}
          rel={cta2.openInNewTab ? 'noopener noreferrer' : undefined}
        >
          <CTA2TopText fontStyle="label-s" as="span">
            {cta2.title}
          </CTA2TopText>
          <CTA2BottomText fontStyle="label-s" as="span">
            {cta2.link?.replace(/^https?:\/\//i, '')}
          </CTA2BottomText>
        </CTA2>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1.25fr 1fr 0.75fr 1fr;
  grid-template-rows: repeat(2, auto);
  grid-template-areas:
    'logo   linkList linkList linkList'
    'social cta1  .  cta2';
  padding-bottom: 2.5rem;
  gap: 5rem 2rem;

  @media (max-width: 1279px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'logo'
      'linkList'
      'cta1'
      'social'
      'cta2';
    gap: 2rem;
  }
`

const LogoLink = styled(Link)`
  @media (max-width: 1279px) {
    display: flex;
    justify-content: center;
  }
`
const Logo = styled.img`
  display: block;
  width: 12rem;
`

const StyledSocialLinks = styled(SocialLinksComponent)`
  grid-area: social;
`

const CTA1 = styled(Button)`
  grid-area: cta1;
  white-space: nowrap;
`

const CTA2 = styled(Button)`
  grid-area: cta2;
  white-space: nowrap;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 0;
  padding-block: 0;
  align-items: flex-start;

  @media (max-width: 1279px) {
    min-height: 3.625rem;
    align-items: center;
  }
`

const CTA2TopText = styled(Text)``
const CTA2BottomText = styled(Text)`
  color: var(--neutral-0);
  font-weight: var(--medium);
`

export default MainFooterSection
